import authService from './authService';

/**
 * MCP Client Service - Connects to MCP gateways to list and call tools
 * Uses the Streamable HTTP transport to communicate with MCP servers
 * Supports multiple simultaneous gateway connections
 */
class MCPService {
  constructor() {
    // Map of gatewayUrl -> { sessionId, tools }
    this.connectedGateways = new Map();
    // Track active fetch requests
    this.activeAbortControllers = new Set();
  }

  /**
   * Abort all active requests
   */
  abortAll() {
    console.log(`Aborting ${this.activeAbortControllers.size} active MCP requests`);
    for (const controller of this.activeAbortControllers) {
      try {
        controller.abort();
      } catch (err) {
        console.error('Error aborting controller:', err);
      }
    }
    this.activeAbortControllers.clear();
  }

  /**
   * Create and register an AbortController for a request
   */
  createAbortController() {
    const controller = new AbortController();
    this.activeAbortControllers.add(controller);
    return controller;
  }

  /**
   * Remove an AbortController from tracking (when request completes)
   */
  removeAbortController(controller) {
    this.activeAbortControllers.delete(controller);
  }

  /**
   * Get the access token for authentication
   * MCP gateways require access tokens, not ID tokens
   */
  async getAccessToken() {
    return await authService.getAccessToken();
  }

  /**
   * Connect to an MCP gateway and initialize the session
   * @param {string} gatewayUrl - The MCP gateway URL (e.g., https://{gatewayId}.gateway.bedrock-agentcore.{region}.amazonaws.com/mcp)
   */
  async connect(gatewayUrl) {
    // If already connected, return existing connection
    if (this.connectedGateways.has(gatewayUrl)) {
      console.log(`Already connected to ${gatewayUrl}`);
      return this.connectedGateways.get(gatewayUrl);
    }

    try {
      const accessToken = await this.getAccessToken();
      
      // Initialize the MCP session
      const initResponse = await fetch(gatewayUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 1,
          method: 'initialize',
          params: {
            protocolVersion: '2024-11-05',
            capabilities: {},
            clientInfo: {
              name: 'BedrockChat',
              version: '1.0.0',
            },
          },
        }),
      });

      if (!initResponse.ok) {
        const errorText = await initResponse.text();
        throw new Error(`MCP initialize failed: ${initResponse.status} - ${errorText}`);
      }

      const initResult = await initResponse.json();
      console.log('MCP Initialize response:', initResult);

      // Store connection info
      this.connectedGateways.set(gatewayUrl, {
        sessionId: initResult.result?.sessionId || null,
        tools: [],
      });

      return initResult.result;
    } catch (error) {
      console.error('Error connecting to MCP gateway:', error);
      throw error;
    }
  }

  /**
   * List available tools from a specific MCP gateway
   * @param {string} gatewayUrl - The MCP gateway URL
   */
  async listTools(gatewayUrl) {
    if (!this.connectedGateways.has(gatewayUrl)) {
      throw new Error(`Not connected to ${gatewayUrl}. Call connect() first.`);
    }

    try {
      const accessToken = await this.getAccessToken();

      const response = await fetch(gatewayUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 2,
          method: 'tools/list',
          params: {},
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`MCP tools/list failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('MCP tools/list response:', result);

      const tools = result.result?.tools || [];
      
      // Update stored tools for this gateway
      const connection = this.connectedGateways.get(gatewayUrl);
      connection.tools = tools;
      
      return tools;
    } catch (error) {
      console.error('Error listing MCP tools:', error);
      throw error;
    }
  }

  /**
   * Call a tool - automatically routes to the correct gateway
   * @param {string} toolName - The name of the tool to call
   * @param {object} args - The arguments to pass to the tool
   */
  async callTool(toolName, args = {}) {
    // Find which gateway has this tool
    let targetGateway = null;
    for (const [gatewayUrl, connection] of this.connectedGateways.entries()) {
      if (connection.tools.some(tool => tool.name === toolName)) {
        targetGateway = gatewayUrl;
        break;
      }
    }

    if (!targetGateway) {
      throw new Error(`Tool '${toolName}' not found in any connected gateway.`);
    }

    const abortController = this.createAbortController();
    
    try {
      const accessToken = await this.getAccessToken();

      const response = await fetch(targetGateway, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 3,
          method: 'tools/call',
          params: {
            name: toolName,
            arguments: args,
          },
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`MCP tools/call failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('MCP tools/call response:', result);

      return result.result;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('MCP tool call aborted');
        throw new Error('Tool call was aborted');
      }
      console.error('Error calling MCP tool:', error);
      throw error;
    } finally {
      this.removeAbortController(abortController);
    }
  }

  /**
   * Convert MCP tool schemas to Bedrock tool specifications
   * Aggregates tools from all connected gateways
   * @returns {Array} Bedrock tool specifications
   */
  getBedrockToolSpecs() {
    const allTools = this.getAllTools();
    return allTools.map(tool => ({
      toolSpec: {
        name: tool.name,
        description: tool.description || '',
        inputSchema: {
          json: tool.inputSchema || { type: 'object', properties: {} },
        },
      },
    }));
  }

  /**
   * Disconnect from a specific MCP gateway
   * @param {string} gatewayUrl - The gateway URL to disconnect from
   */
  disconnect(gatewayUrl) {
    if (gatewayUrl) {
      this.connectedGateways.delete(gatewayUrl);
    } else {
      // Disconnect all if no specific gateway provided
      this.connectedGateways.clear();
    }
  }

  /**
   * Get all connected gateway URLs
   * @returns {Array<string>} Array of connected gateway URLs
   */
  getConnectedGateways() {
    return Array.from(this.connectedGateways.keys());
  }

  /**
   * Get all tools from all connected gateways
   * @returns {Array} All tools
   */
  getAllTools() {
    const allTools = [];
    for (const connection of this.connectedGateways.values()) {
      allTools.push(...connection.tools);
    }
    return allTools;
  }

  /**
   * Check if a gateway is connected
   * @param {string} gatewayUrl - The gateway URL to check
   * @returns {boolean} True if connected
   */
  isConnected(gatewayUrl) {
    return this.connectedGateways.has(gatewayUrl);
  }
}

export default new MCPService();
